import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useRole } from '../context/roleContext';
import { USER_ROLES } from '../utils/constants';
import useDeliveryOrders from '../hooks/useDeliveryOrders';
import DeliveryChallanActions from '../components/delivery-challan/DeliveryChallanActions';
import DeliveryChallanTable from '../components/delivery-challan/DeliveryChallanTable';
import { useErrorDialog } from '../context/errorDialogContext';
import { useWarehouseContext } from '../context/WarehouseContext';
import Pagination from '../components/common/Pagination';
import { UI_COLORS, colorHelpers } from '../utils/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt } from '@fortawesome/free-solid-svg-icons';
import DeliveryOrderViewModal from '../components/delivery-orders/DeliveryOrderViewModal';
import deliveryOrderService from '../services/deliveryOrderService';
import ChallanSuccessModal from '../components/delivery-orders/ChallanSuccessModal';
import ExportModal from '../components/common/ExportModal';
import EditDeliveryChallanModal from '../components/delivery-challan/EditDeliveryChallanModal';
import { useMessageAlert } from '../components/common/MessageAlert';
import { printTableData } from '../utils/tablePrintHelper';
import { useTranslation } from 'react-i18next';

const DEFAULT_PAGE_LIMIT = 20;
const PAGE_SIZE_STORAGE_KEY = 'delivery_challan_page_size';

const DeliveryChallanPage = () => {
    const { t } = useTranslation();
    const {
        orders,
        total,
        loading,
        error,
        fetchOrders,
        markDelivered,
        fetchOrderById,
        exportChallans,
        editChallan,
        markChallanPrinted,
    } = useDeliveryOrders('/delivery-challans');

    const { userRole } = useRole();

    const [searchTerm, setSearchTerm] = useState('');
    const [committedSearch, setCommittedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const today = new Date().toLocaleDateString('en-CA');
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(() => {
        const saved = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        return saved ? Number(saved) : DEFAULT_PAGE_LIMIT;
    });
    const searchDebounceRef = useRef(null);

    const [viewingOrder, setViewingOrder] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);

    const [showChallanSuccessModal, setShowChallanSuccessModal] = useState(false);
    const [challanResultData, setChallanResultData] = useState(null);
    const [challanSubOrderId, setChallanSubOrderId] = useState(null);

    const [showExportModal, setShowExportModal] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editingOrderId, setEditingOrderId] = useState(null);
    const [editLoading, setEditLoading] = useState(false);

    const { showSuccess } = useMessageAlert();

    const { showError } = useErrorDialog();
    const { selectedWarehouseIds } = useWarehouseContext();
    const strings = t('deliveryChallanModule', { returnObjects: true });

    // Debounce search
    useEffect(() => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setCommittedSearch(searchTerm);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(searchDebounceRef.current);
    }, [searchTerm]);

    // Fetch only in_transit and delivered
    useEffect(() => {
        const skip = (currentPage - 1) * pageSize;
        const params = { skip, limit: pageSize, challan_printed: false };
        if (committedSearch) params.search = committedSearch;
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;

        params.status = statusFilter ? [statusFilter] : ['in_transit', 'delivered'];

        fetchOrders(params);
    }, [fetchOrders, currentPage, committedSearch, statusFilter, fromDate, toDate, selectedWarehouseIds, pageSize]);

    useEffect(() => {
        if (error) showError(error, t('deliveryChallans.errors.errorTitle'));
    }, [error, showError, t]);

    const filteredOrders = useMemo(() => {
        const baseOrders = orders || [];

        return [...baseOrders].sort((a, b) => {
            const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
            const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
            return dateB - dateA;
        });
    }, [orders]);

    const handleStatusChange = (val) => {
        setStatusFilter(val);
        setCurrentPage(1);
    };

    const handleFromDateChange = (val) => {
        setFromDate(val);
        setCurrentPage(1);
    };

    const handleToDateChange = (val) => {
        setToDate(val);
        setCurrentPage(1);
    };

    const handleRefresh = async () => {
        const skip = (currentPage - 1) * pageSize;
        const params = { skip, limit: pageSize, challan_printed: false };
        if (committedSearch) params.search = committedSearch;
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;
        params.status = statusFilter ? [statusFilter] : ['in_transit', 'delivered'];
        await fetchOrders(params);
    };

    const handlePrintPage = () => {
        const columns = [
            { id: 'challan_number', label: strings.table.columns.challanNumber },
            { id: 'order_number', label: strings.table.columns.orderNumber },
            { id: 'sub_order_number', label: strings.table.columns.subOrderNumber },
            { id: 'tally_name', label: strings.table.columns.tallyName },
            { id: 'location_name', label: strings.table.columns.location },
            { id: 'vehicle_number', label: t('deliveryChallans.labels.vehicleNumber') },
            { id: 'total_quantity', label: strings.table.columns.totalQty },
            { id: 'status', label: strings.table.columns.status },
        ];
        printTableData(filteredOrders, columns, strings.page.header.title);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(1);
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, newSize.toString());
    };

    const handleExport = async ({ from_date, to_date, format }) => {
        setExportLoading(true);
        try {
            const result = await exportChallans({ 
                from_date, 
                to_date, 
                format,
                ...(committedSearch && { search: committedSearch }),
                status: statusFilter ? [statusFilter] : ['in_transit', 'delivered']
            });
            if (result.success) {
                setShowExportModal(false);
            } else {
                showError(result.error || t('deliveryChallans.errors.exportFailed'), t('deliveryChallans.errors.errorTitle'));
            }
        } finally {
            setExportLoading(false);
        }
    };

    const handleViewOrder = async (subOrder) => {
        if (subOrder.id) {
            setViewLoading(true);
            setViewingOrder(null);
            setShowViewModal(true);
            try {
                const result = await fetchOrderById(subOrder.challan_number || subOrder.id);
                if (result.success && result.data) setViewingOrder(result.data);
                else showError(result.error || t('deliveryChallans.errors.failedToFetchOrderDetails'), t('deliveryChallans.errors.errorTitle'));
            } catch (err) {
                showError(err.message || t('deliveryChallans.errors.failedToFetchOrderDetails'), t('deliveryChallans.errors.errorTitle'));
            } finally {
                setViewLoading(false);
            }
        }
    };

    const handlePrintChallan = async (order) => {
        const result = await fetchOrderById(order.challan_number || order.id);
        if (result.success) {
            setChallanResultData(result.data || null);
            setChallanSubOrderId(order.id);
            setShowChallanSuccessModal(true);
        } else {
            showError(result.error || t('deliveryChallans.errors.failedToFetchOrderDetails'), t('deliveryChallans.errors.errorTitle'));
        }
    };

    const handleEditChallan = (order) => {
        setEditingOrderId(order.id);
        setShowEditModal(true);
    };

    const handleConfirmEdit = async (id, payload) => {
        setEditLoading(true);
        try {
            const result = await editChallan(id, payload);
            if (result.success) {
                showSuccess(strings.actions.editChallanSuccess);
                setShowEditModal(false);
                setEditingOrderId(null);
                handleRefresh();
            } else {
                showError(result.error || strings.actions.editChallanFailed, t('deliveryChallans.errors.errorTitle'));
            }
        } finally {
            setEditLoading(false);
        }
    };

    // Real total calculation (safety measure for backend total inconsistencies)
    const realTotal = useMemo(() => {
        if (currentPage === 1 && filteredOrders.length < pageSize && total > filteredOrders.length) {
            return filteredOrders.length;
        }
        return total;
    }, [total, filteredOrders.length, currentPage, pageSize]);

    const totalPages = Math.ceil(realTotal / pageSize);

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-3xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                        {strings.page.header.title}
                    </h1>
                    <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                        {strings.page.header.subtitle}
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                    <div
                        className="flex items-center gap-3 rounded-2xl border px-4 py-3"
                        style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                    >
                        <div
                            className="flex h-12 w-12 items-center justify-center rounded-full"
                            style={{
                                backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.15),
                                color: UI_COLORS.BUTTONS.PRIMARY.background,
                            }}
                        >
                            <FontAwesomeIcon icon={faFileAlt} />
                        </div>
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                {strings.page.stats.total}
                            </p>
                            <p className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                {realTotal}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <DeliveryChallanActions
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                onRefresh={handleRefresh}
                refreshLoading={loading}
                onPrint={handlePrintPage}
                statusValue={statusFilter}
                onStatusChange={handleStatusChange}
                fromDate={fromDate}
                toDate={toDate}
                onFromDateChange={handleFromDateChange}
                onToDateChange={handleToDateChange}
                onExport={() => setShowExportModal(true)}
                exportLoading={exportLoading}
                canExport={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN, USER_ROLES.ACCOUNT_USER, USER_ROLES.SALES_STAFF, USER_ROLES.OPERATIONS_MANAGER].includes(userRole)}
            />

            <div className="bg-white shadow-sm rounded-lg border border-gray-200 mt-6">
                <DeliveryChallanTable
                    orders={filteredOrders}
                    loading={loading}
                    onViewOrder={handleViewOrder}
                    onMarkDelivered={markDelivered}
                    onPrintChallan={handlePrintChallan}
                    onSendChallanNotification={(id, type) => deliveryOrderService.sendChallanNotification(id, type)}
                    onEditChallan={handleEditChallan}
                    onRefresh={fetchOrders}
                />
            </div>

            {/* Pagination */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={realTotal}
                onPageChange={setCurrentPage}
                loading={loading}
                totalLabel="total challans"
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
            />

            {showViewModal && (
                <DeliveryOrderViewModal
                    isOpen={showViewModal}
                    onClose={() => { setShowViewModal(false); setViewingOrder(null); }}
                    order={viewingOrder}
                    loading={viewLoading}
                />
            )}

            {showChallanSuccessModal && (
                <ChallanSuccessModal
                    isOpen={showChallanSuccessModal}
                    data={challanResultData}
                    onClose={() => { setShowChallanSuccessModal(false); setChallanResultData(null); setChallanSubOrderId(null); }}
                    onSendNotification={challanSubOrderId ? (type) => deliveryOrderService.sendChallanNotification(challanSubOrderId, type) : undefined}
                    onMarkChallanPrinted={challanSubOrderId ? () => markChallanPrinted(challanSubOrderId) : undefined}
                    userRole={userRole}
                    onEditSuccess={() => { fetchOrders(); }}
                />
            )}

            {showExportModal && (
                <ExportModal
                    isOpen={showExportModal}
                    onClose={() => setShowExportModal(false)}
                    onExport={handleExport}
                    loading={exportLoading}
                    title={t('deliveryChallans.export.title')}
                />
            )}

            {showEditModal && (
                <EditDeliveryChallanModal
                    isOpen={showEditModal}
                    onClose={() => { setShowEditModal(false); setEditingOrderId(null); }}
                    subOrderId={editingOrderId}
                    onConfirm={handleConfirmEdit}
                    loading={editLoading}
                />
            )}
        </div>
    );
};

export default DeliveryChallanPage;
